import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Key,
  Delete,
  Email,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Add,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { parseDate } from '../utils/dateUtils';
import { partnerService } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';

interface ApiKey {
  id: string;
  keyPrefix: string;
  description?: string;
  isActive?: boolean;
  status?: string;
  active?: boolean;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
}

interface ApiKeyManagementProps {
  partnerId: string;
  callbackUrl?: string;
  partnerType?: string;
}

const ApiKeyManagement: React.FC<ApiKeyManagementProps> = ({ partnerId, callbackUrl, partnerType }) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [keyDescription, setKeyDescription] = useState('');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [apiUsage, setApiUsage] = useState<any>(null);

  useEffect(() => {
    fetchApiKeys();
    fetchApiUsage();
  }, [partnerId]);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await partnerService.getApiKeys(partnerId);
      console.log('API Keys Response:', response);
      console.log('First key (if exists):', response[0]);
      setApiKeys(response);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load API keys');
      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const response = await partnerService.getApiUsage(partnerId);
      setApiUsage(response);
    } catch (error) {
      console.error('Failed to fetch API usage:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load API usage');
      showNotification('error', errorMessage);
    }
  };

  const handleGenerateKey = async () => {
    try {
      const response = await partnerService.generateApiKey(partnerId);
      setGeneratedKey(response.apiKey);
      setShowKeyDialog(true);
      setShowGenerateDialog(false);
      setKeyDescription('');
      await fetchApiKeys();
      showNotification('success', 'API key generated successfully');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to generate API key');
      showNotification('error', errorMessage);
    }
  };

  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);

  const handleRevokeKey = async () => {
    if (!selectedKeyId) return;

    try {
      await partnerService.revokeApiKey(partnerId, selectedKeyId);
      await fetchApiKeys();
      showNotification('success', 'API key revoked successfully');
      setRevokeDialogOpen(false);
      setSelectedKeyId(null);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to revoke API key');
      showNotification('error', errorMessage);
    }
  };

  const openRevokeDialog = (keyId: string) => {
    setSelectedKeyId(keyId);
    setRevokeDialogOpen(true);
  };

  const handleSendEmail = async (keyId: string) => {
    try {
      await partnerService.sendApiKeyEmail(partnerId, keyId);
      showNotification('success', 'API key sent to partner email');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to send email');
      showNotification('error', errorMessage);
    }
  };

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      showNotification('success', 'API key copied to clipboard');
    }
  };

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Helper function to determine if a key is active - handles different backend formats
  const isKeyActive = (key: ApiKey): boolean => {
    // Check all possible ways the backend might indicate active status
    if (key.isActive !== undefined) return key.isActive;
    if (key.active !== undefined) return key.active;
    if (key.status !== undefined) return key.status === 'ACTIVE' || key.status === 'Active';
    // Default to true if no status field exists (newly created keys)
    return true;
  };

  const getStatusColor = (isActive: boolean): 'success' | 'default' => {
    return isActive ? 'success' : 'default';
  };

  return (
    <Box>
      {notification && (
        <Alert severity={notification.type} sx={{ mb: 2 }} onClose={() => setNotification(null)}>
          {notification.message}
        </Alert>
      )}

      {/* API Usage Statistics */}
      {apiUsage && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Total Requests (24h)
                </Typography>
                <Typography variant="h5">
                  {apiUsage.totalRequests24h?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Success Rate
                </Typography>
                <Typography variant="h5">
                  {apiUsage.successRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Last Request
                </Typography>
                <Typography variant="h6">
                  {apiUsage.lastRequestAt
                    ? format(parseDate(apiUsage.lastRequestAt), 'MMM dd, HH:mm')
                    : 'Never'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Active Keys
                </Typography>
                <Typography variant="h5">
                  {apiKeys.filter(k => isKeyActive(k)).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Callback URL Configuration (Merchants Only) */}
      {partnerType === 'MERCHANT' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Callback Configuration
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Callback URL"
                secondary={callbackUrl || 'Not configured - No callback notifications will be sent'}
                secondaryTypographyProps={{
                  sx: {
                    fontFamily: callbackUrl ? 'monospace' : 'inherit',
                    wordBreak: 'break-all'
                  }
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Description"
                secondary="This URL will receive POST requests with payment notifications when vouchers are paid by customers. Configure this in the Edit Partner page."
              />
            </ListItem>
          </List>
        </Paper>
      )}

      {/* API Keys Table */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">API Keys</Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchApiKeys}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowGenerateDialog(true)}
            >
              Generate New Key
            </Button>
          </Box>
        </Box>

        <TableContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : apiKeys.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                No API keys found. Generate a new key to get started.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key Prefix</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {key.keyPrefix}***
                      </Typography>
                    </TableCell>
                    <TableCell>{key.description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={isKeyActive(key) ? 'Active' : 'Revoked'}
                        color={getStatusColor(isKeyActive(key))}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(parseDate(key.createdAt), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {key.lastUsedAt
                        ? format(parseDate(key.lastUsedAt), 'MMM dd, yyyy HH:mm')
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      {key.expiresAt
                        ? format(parseDate(key.expiresAt), 'MMM dd, yyyy')
                        : 'Never'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={isKeyActive(key) ? "Send via Email" : "Key is revoked"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSendEmail(key.id)}
                            disabled={!isKeyActive(key)}
                          >
                            <Email />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isKeyActive(key) ? "Revoke Key" : "Key is already revoked"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openRevokeDialog(key.id)}
                            disabled={!isKeyActive(key)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* Additional Information */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          API Configuration
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="API Base URL"
              secondary={process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/vouchers'}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Authentication"
              secondary="Include API key in the 'X-API-Key' header for all requests"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Rate Limits"
              secondary="1000 requests per hour per API key"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Generate Key Dialog */}
      <Dialog open={showGenerateDialog} onClose={() => setShowGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The API key will only be displayed once. Make sure to copy and store it securely.
          </Alert>
          <TextField
            fullWidth
            label="Description (Optional)"
            value={keyDescription}
            onChange={(e) => setKeyDescription(e.target.value)}
            placeholder="e.g., Production API Key"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
          <Button onClick={handleGenerateKey} variant="contained">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={showKeyDialog} onClose={() => setShowKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your API key has been generated successfully. Copy it now - you won't be able to see it again!
          </Alert>
          <TextField
            fullWidth
            value={generatedKey || ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyKey}>
                  <ContentCopy />
                </IconButton>
              ),
            }}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowKeyDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Key Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to revoke this API key? This action cannot be undone and the key will immediately stop working.
          </Alert>
          <Typography variant="body2">
            Any applications using this API key will no longer be able to authenticate. Make sure you have updated your applications before revoking this key.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRevokeKey} variant="contained" color="error">
            Revoke Key
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeyManagement;
